/**
 * SkillsPage — F-SKILL-1 Packet B1 + B2 + B3
 *
 * AC-SKILL-01: Browse all org skills (name, category, System/Custom badge,
 *              # assigned operators, active state).
 * AC-SKILL-02: Text search + category filter.
 * AC-SKILL-03: System vs Custom badge.
 * AC-SKILL-04: Admin can open the create form.
 * AC-SKILL-11 (F): Admin edits a custom skill (row click → edit mode).
 * AC-SKILL-13 (F): Admin deletes a custom skill (with confirm dialog).
 * AC-SKILL-14 (F): Matrix tab — assign/unassign skills to operators.
 * AC-SKILL-25 (F): View-only users see no mutate controls.
 *
 * Reads from /api/v1/agent-skills (list+filter) and /api/v1/agent-skills/matrix
 * (assignment counts). Org-scoping is handled by apiFetch via X-Organization-Id.
 */

import React, { useMemo, useState } from 'react';
import { BookOpen, Plus, Pencil, Trash2, Grid3X3, List } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { useRorosSkills, useRorosSkillMatrix, useRorosSkillDelete } from '@/hooks/roros/skills';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { SkillFormDialog } from './SkillFormDialog';
import { SkillsMatrix } from './SkillsMatrix';
import type { SkillCategory, RorosSkillDTO } from '@/types/roros/skills';
import type { RorosSkillListItem } from '@/services/roros/skills.service';
import { SkillsService } from '@/services/roros/skills.service';

// ─── Category options ─────────────────────────────────────────────────────────

// BUG-1 FIX: Radix Select forbids value="" on SelectItem — use a non-empty
// sentinel ('all') for the "no filter" option. Map 'all' → undefined before
// passing to the list query so the API receives no category param.
const CATEGORY_SENTINEL = 'all' as const;
type CategoryFilter = SkillCategory | typeof CATEGORY_SENTINEL;

// i18n-1 FIX: CATEGORY_OPTIONS was built at module scope with t() — frozen to
// EN on first load. Moved inside the component so t() re-evaluates on render
// with the current locale. See function getCategoryOptions() below.
function getCategoryOptions() {
  return [
    { value: CATEGORY_SENTINEL, label: t('All categories') },
    { value: 'infrastructure', label: t('Infrastructure') },
    { value: 'tool', label: t('Tool') },
    { value: 'integration', label: t('Integration') },
    { value: 'domain', label: t('Domain') },
    { value: 'quality', label: t('Quality') },
    { value: 'content', label: t('Content') },
  ];
}

type PageView = 'list' | 'matrix';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function categoryLabel(cat: string): string {
  const map: Record<string, string> = {
    infrastructure: t('Infrastructure'),
    tool: t('Tool'),
    integration: t('Integration'),
    domain: t('Domain'),
    quality: t('Quality'),
    content: t('Content'),
  };
  return map[cat] ?? cat;
}

function SystemBadge({ isSystem }: { isSystem: boolean }) {
  return isSystem ? (
    <Badge variant="secondary" data-testid="skill-badge-system">
      {t('System')}
    </Badge>
  ) : (
    <Badge variant="outline" data-testid="skill-badge-custom">
      {t('Custom')}
    </Badge>
  );
}

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <Badge variant="success" data-testid="skill-active-badge">
      {t('Active')}
    </Badge>
  ) : (
    <Badge variant="secondary" data-testid="skill-inactive-badge">
      {t('Inactive')}
    </Badge>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SkillsPage() {
  const [search, setSearch] = useState('');
  // BUG-1 FIX: default to sentinel 'all' (never empty string — Radix throws on value="")
  const [category, setCategory] = useState<CategoryFilter>(CATEGORY_SENTINEL);

  // View toggle: library list vs assignment matrix
  const [view, setView] = useState<PageView>('list');

  // Create form
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Edit form
  const [editSkill, setEditSkill] = useState<RorosSkillDTO | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<RorosSkillListItem | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_AGENT_SKILLS_MANAGE);

  // i18n-1 FIX: build at render-time so locale changes are reflected
  const CATEGORY_OPTIONS = getCategoryOptions();

  // Map sentinel back to undefined so the API receives no category param
  const categoryParam = category === CATEGORY_SENTINEL ? undefined : (category as SkillCategory);

  // BUG-2 FIX: API schema caps pageSize at 100; passing 200 → 422.
  const listQuery = useRorosSkills({
    search: search || undefined,
    category: categoryParam,
    pageSize: 100,
  });

  const matrixQuery = useRorosSkillMatrix();
  const deleteMutation = useRorosSkillDelete();

  // Build a map from skillId → assigned operator count (from matrix data)
  const assignedCountBySkillId = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    for (const entry of matrixQuery.data ?? []) {
      map.set(entry.skillId, entry.assignedAgents.length);
    }
    return map;
  }, [matrixQuery.data]);

  const skills = listQuery.data?.data ?? [];

  // ─── Handlers ───────────────────────────────────────────────────────────

  async function handleEditOpen(skill: RorosSkillListItem) {
    // Fetch full detail (includes content field)
    try {
      const detail = await SkillsService.get(skill.id);
      setEditSkill(detail);
      setIsEditOpen(true);
    } catch {
      showToast.error(t('Failed to load skill'));
    }
  }

  function handleEditClose() {
    setIsEditOpen(false);
    setEditSkill(null);
  }

  function handleDeleteClick(skill: RorosSkillListItem) {
    setDeleteTarget(skill);
    setIsDeleteConfirmOpen(true);
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showToast.success(t('Skill deleted'));
      setIsDeleteConfirmOpen(false);
      setDeleteTarget(null);
      listQuery.refetch();
    } catch {
      showToast.error(t('Failed to delete skill'));
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <RorosPage title={t('Skills')}>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        {/* View toggle */}
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5 bg-muted/30">
          <Button
            type="button"
            variant={view === 'list' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
            data-testid="skills-view-list-button"
            aria-label={t('Library view')}
          >
            <List className="w-4 h-4 mr-1" />
            {t('Library')}
          </Button>
          <Button
            type="button"
            variant={view === 'matrix' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setView('matrix')}
            aria-pressed={view === 'matrix'}
            data-testid="skills-view-matrix-button"
            aria-label={t('Matrix view')}
          >
            <Grid3X3 className="w-4 h-4 mr-1" />
            {t('Matrix')}
          </Button>
        </div>

        {view === 'list' && (
          <>
            <div className="flex-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Search skills…')}
                data-testid="skills-search-input"
                aria-label={t('Search skills')}
              />
            </div>
            <div className="w-48">
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as CategoryFilter)}
                options={CATEGORY_OPTIONS}
                data-testid="skills-category-filter"
                aria-label={t('Filter by category')}
              />
            </div>
            {canManage && (
              <Button
                onClick={() => setIsCreateOpen(true)}
                data-testid="skills-new-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('New Skill')}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Matrix view */}
      {view === 'matrix' && (
        <SkillsMatrix />
      )}

      {/* List / Table view */}
      {view === 'list' && <QueryPage
        query={{
          data: skills.length > 0 ? skills : (listQuery.isLoading ? undefined : []),
          isLoading: listQuery.isLoading,
          isError: listQuery.isError,
          error: listQuery.error,
          refetch: listQuery.refetch,
        }}
        emptyMessage={t('No skills found')}
        emptyIcon={<BookOpen className="w-8 h-8 text-muted-foreground" />}
        emptyAction={
          canManage ? (
            <Button onClick={() => setIsCreateOpen(true)} data-testid="skills-empty-new-button">
              {t('Create first skill')}
            </Button>
          ) : undefined
        }
      >
        {() => (
          <div className="overflow-x-auto rounded-md border border-border">
            <table
              className="w-full min-w-[700px] text-sm"
              aria-label={t('Skills library')}
              data-testid="skills-table"
            >
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">
                    {t('Name')}
                  </th>
                  <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-32">
                    {t('Category')}
                  </th>
                  <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">
                    {t('Type')}
                  </th>
                  <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-36">
                    {t('Assigned operators')}
                  </th>
                  <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-24">
                    {t('State')}
                  </th>
                  {canManage && (
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">
                      {t('Actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {skills.map((skill) => (
                  <tr
                    key={skill.id}
                    className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                    data-testid="skill-row"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{skill.displayName}</div>
                      <div className="text-xs text-muted-foreground font-mono">{skill.name}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {categoryLabel(skill.category)}
                    </td>
                    <td className="px-4 py-3">
                      <SystemBadge isSystem={skill.isSystem} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-sm" data-testid="skill-assigned-count">
                      {assignedCountBySkillId.get(skill.id) ?? 0}
                    </td>
                    <td className="px-4 py-3">
                      <ActiveBadge isActive={skill.isActive} />
                    </td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {/* Edit — only for custom skills */}
                          {!skill.isSystem && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditOpen(skill)}
                              aria-label={t('Edit skill')}
                              data-testid="skill-edit-button"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Delete — only for custom skills */}
                          {!skill.isSystem && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteClick(skill)}
                              aria-label={t('Delete skill')}
                              data-testid="skill-delete-button"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryPage>}

      {/* Create skill dialog */}
      <SkillFormDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSaved={() => listQuery.refetch()}
        canManage={canManage}
      />

      {/* Edit skill dialog */}
      <SkillFormDialog
        isOpen={isEditOpen}
        onClose={handleEditClose}
        onSaved={() => listQuery.refetch()}
        skill={editSkill}
        canManage={canManage}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('Delete skill?')}
        description={
          deleteTarget
            ? `${t('This will permanently delete')} "${deleteTarget.displayName}". ${t('The skill will be unassigned from all operators.')}`
            : t('This will permanently delete the skill.')
        }
        confirmText={t('Delete')}
        variant="destructive"
        isLoading={deleteMutation.isPending}
        confirmTestId="skill-delete-confirm-button"
        cancelTestId="skill-delete-cancel-button"
      />
    </RorosPage>
  );
}
